import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './Pages.css';

const PractitionerCRM = ({ user, apiUrl, token }) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientHistory, setClientHistory] = useState([]);
  const [clientNotes, setClientNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const userRole = user?.role || JSON.parse(localStorage.getItem('user') || '{}')?.role;
  const userId = user?.id || user?.uuid || JSON.parse(localStorage.getItem('user') || '{}')?.uuid;

  useEffect(() => {
    if (userRole !== 'practitioner' && userRole !== 'admin') {
      navigate('/');
      return;
    }
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, navigate]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/practitioners/${userId}/clients`, { headers });
      setClients(response.data || []);
    } catch (err) {
      setError('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  const revenueTotals = useMemo(() => {
    const sum = (statuses) => clientHistory
      .filter((b) => statuses.includes(b.status))
      .reduce((total, b) => total + (parseFloat(b.amount) || 0), 0);
    const paid = sum(['confirmed', 'completed']);
    const open = sum(['awaiting_payment']);
    return { paid, open, total: paid + open };
  }, [clientHistory]);

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.tags || []).some((tag) => tag.toLowerCase().includes(q))
    );
  }, [clients, search]);

  const exportCSV = () => {
    const rows = [['Name', 'Email', 'Tags'], ...filteredClients.map((c) => [c.name || '', c.email || '', (c.tags || []).join('; ')])];
    const csv = rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'clients.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const openClient = async (client) => {
    setSelectedClient(client);
    setError('');
    try {
      const [historyRes, notesRes] = await Promise.all([
        axios.get(`${apiUrl}/practitioners/${userId}/clients/${client.id}/history`, { headers }),
        axios.get(`${apiUrl}/crm/notes/${client.id}`, { headers })
      ]);
      setClientHistory(historyRes.data || []);
      setClientNotes(notesRes.data || []);
    } catch (err) {
      setError('Failed to load client details');
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedClient) return;
    try {
      await axios.post(`${apiUrl}/practitioners/${userId}/clients/${selectedClient.id}/notes`, { note: newNote }, { headers });
      setNewNote('');
      const notesRes = await axios.get(`${apiUrl}/crm/notes/${selectedClient.id}`, { headers });
      setClientNotes(notesRes.data || []);
    } catch (err) {
      setError('Failed to add note');
    }
  };

  const addTag = async () => {
    if (!newTag.trim() || !selectedClient) return;
    try {
      const response = await axios.post(
        `${apiUrl}/practitioners/${userId}/clients/${selectedClient.id}/tags`,
        { tag: newTag.trim() },
        { headers }
      );
      setNewTag('');
      setSelectedClient({ ...selectedClient, tags: response.data });
      setClients((prev) => prev.map((c) => (c.id === selectedClient.id ? { ...c, tags: response.data } : c)));
    } catch (err) {
      setError('Failed to add tag');
    }
  };

  const removeTag = async (tag) => {
    if (!selectedClient) return;
    try {
      await axios.delete(`${apiUrl}/practitioners/${userId}/clients/${selectedClient.id}/tags/${encodeURIComponent(tag)}`, { headers });
      const updatedTags = (selectedClient.tags || []).filter((t) => t !== tag);
      setSelectedClient({ ...selectedClient, tags: updatedTags });
      setClients((prev) => prev.map((c) => (c.id === selectedClient.id ? { ...c, tags: updatedTags } : c)));
    } catch (err) {
      setError('Failed to remove tag');
    }
  };

  if (loading) return <div className="page-container">Loading clients...</div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Client CRM</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/practitioner/dashboard')}>
          Back to Dashboard
        </button>
      </div>
      {error && <div className="error-message">{error}</div>}

      <div className="flex-between" style={{ marginBottom: 14 }}>
        <input
          placeholder="Search by name, email, or tag..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <button className="btn btn-secondary btn-sm" onClick={exportCSV}>Export CSV</button>
      </div>

      <div className="list-container table-wrap">
        <table className="table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Tags</th><th></th></tr>
          </thead>
          <tbody>
            {filteredClients.map((client) => (
              <tr key={client.id}>
                <td>{client.name || <span className="text-muted">—</span>}</td>
                <td>{client.email}</td>
                <td>
                  {(client.tags || []).length === 0 ? (
                    <span className="text-muted">—</span>
                  ) : (
                    client.tags.map((tag) => (
                      <span key={tag} className="badge badge-practitioner" style={{ marginRight: 4 }}>{tag}</span>
                    ))
                  )}
                </td>
                <td>
                  <button className="btn btn-primary btn-sm" onClick={() => openClient(client)}>View</button>
                </td>
              </tr>
            ))}
            {filteredClients.length === 0 && <tr><td colSpan="4" className="empty-state">No clients found</td></tr>}
          </tbody>
        </table>
      </div>

      {selectedClient && (
        <div className="modal" onClick={() => setSelectedClient(null)}>
          <div className="modal-content" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedClient.name || selectedClient.email}</h2>
              <button className="close-btn" onClick={() => setSelectedClient(null)}>×</button>
            </div>

            <div className="card-header" style={{ fontSize: '0.9rem' }}>Tags</div>
            <div style={{ marginBottom: 14 }}>
              {(selectedClient.tags || []).map((tag) => (
                <span key={tag} className="badge badge-practitioner" style={{ marginRight: 6, marginBottom: 6, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  {tag}
                  <button onClick={() => removeTag(tag)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontWeight: 700 }}>×</button>
                </span>
              ))}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input placeholder="Add a tag..." value={newTag} onChange={(e) => setNewTag(e.target.value)} style={{ flex: 1 }} />
                <button className="btn btn-secondary btn-sm" onClick={addTag}>Add</button>
              </div>
            </div>

            <div className="card-header" style={{ fontSize: '0.9rem' }}>Booking History</div>
            {clientHistory.length > 0 && (
              <div className="flex-between" style={{ background: '#f5f6fa', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontWeight: 700, color: '#16a34a' }}>${revenueTotals.paid.toFixed(2)}</div>
                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>Paid</div>
                </div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontWeight: 700, color: '#c2366f' }}>${revenueTotals.open.toFixed(2)}</div>
                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>Open</div>
                </div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontWeight: 700 }}>${revenueTotals.total.toFixed(2)}</div>
                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>Total</div>
                </div>
              </div>
            )}
            {clientHistory.length === 0 ? (
              <p className="empty-state">No bookings yet</p>
            ) : (
              clientHistory.map((booking) => (
                <div
                  key={booking.id}
                  className="note"
                  style={{ background: 'var(--color-bg)', cursor: 'pointer' }}
                  onClick={() => navigate(`/bookings/${booking.id}`)}
                >
                  <div className="note-meta">
                    <span>{booking.service_name}</span>
                    <span className={`badge badge-${booking.status}`}>{booking.status}</span>
                  </div>
                  <div className="note-text">
                    {new Date(booking.booking_date).toLocaleString()} · ${parseFloat(booking.amount).toFixed(2)}
                  </div>
                </div>
              ))
            )}

            <div className="card-header" style={{ fontSize: '0.9rem', marginTop: 16 }}>Notes</div>
            {clientNotes.length === 0 ? (
              <p className="empty-state">No notes yet</p>
            ) : (
              clientNotes.map((note) => (
                <div key={note.id} className="note">
                  <div className="note-meta">
                    <span>{note.creator_name}</span>
                    <span>{new Date(note.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="note-text">{note.note}</div>
                </div>
              ))
            )}
            <div style={{ marginTop: 10 }}>
              <textarea
                rows="3"
                placeholder="Add a note about this client..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <button className="btn btn-primary btn-sm" style={{ marginTop: 8 }} onClick={addNote}>Save Note</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PractitionerCRM;
